import { ref, nextTick } from 'vue'

interface SnackbarOptions {
  text: string
  color?: string
  timeout?: number
  location?: 'top' | 'bottom'
  attach?: string
}

// Global singleton state - created once and shared across all components
const globalSnackbarState = {
  isOpen: ref(false),
  text: ref(''),
  color: ref('success'),
  timeout: ref(3000),
  location: ref<'top' | 'bottom'>('top'),
  attach: ref<string | undefined>(undefined)
}

export function useSnackbar() {
  const show = async (options: SnackbarOptions) => {
    // Close + clear attach to invalidate Vuetify's cached teleport target.
    // useTeleport() is a computed that resolves attach once and caches the
    // resulting .v-overlay-container DOM node. When the source drawer
    // unmounts, that container is detached but the cache stays — subsequent
    // shows teleport into invisible DOM. Setting attach to undefined and
    // back forces the computed to re-evaluate and re-query the selector.
    globalSnackbarState.isOpen.value = false
    globalSnackbarState.attach.value = undefined
    await nextTick()
    globalSnackbarState.text.value = options.text
    globalSnackbarState.color.value = options.color || 'success'
    globalSnackbarState.timeout.value = options.timeout || 5000
    globalSnackbarState.location.value = options.location || 'top'
    globalSnackbarState.attach.value = options.attach || undefined
    globalSnackbarState.isOpen.value = true
  }

  const hide = () => {
    globalSnackbarState.isOpen.value = false
  }

  return {
    isOpen: globalSnackbarState.isOpen,
    text: globalSnackbarState.text,
    color: globalSnackbarState.color,
    timeout: globalSnackbarState.timeout,
    location: globalSnackbarState.location,
    attach: globalSnackbarState.attach,
    show,
    hide
  }
}
